/**
 * GrahaChakraRenderer - Specialized renderer for planetary positions (Graha Chakra)
 *
 * Extracted from TabChartRenderer for clean separation of concerns.
 * Handles only planet rendering, hora highlighting, and display modes.
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { EventStream } from '../core/EventStream';
import { ElementPreparationEngine } from '../engines/ElementPreparationEngine';
import { ElementCalculationEngine, UnifiedElement } from '../engines/ElementCalculationEngine';
import { ElementRenderingEngine } from '../engines/ElementRenderingEngine';
import { ContainerBounds } from '../engines/ElementCalculationEngine';
import * as d3 from 'd3';
import { ReadyChartData } from '../../../../../types/ReadyChartData';
import { DEFAULT_PRESET } from '../../../../../config/chartColorPresets';

export interface GrahaRenderRequest {
    chartId: string;
    targetSelector: string;
    data: {
        planets: any[];
        lagna: any;
        hora?: any;
        rawApiPlanets?: any[];
    };
    options: {
        displayMode: 'symbols' | 'short';
        showHora: boolean;
        enableAnimations: boolean;
        debugMode?: boolean;
    };
    containerBounds: Map<string, ContainerBounds>;
}

export interface GrahaRenderResult {
    success: boolean;
    elementsRendered: number;
    renderTime: number;
    chartId: string;
}

/**
 * Specialized renderer for Graha Chakra (planetary positions)
 */
export class GrahaChakraRenderer {
    private eventStream: EventStream;
    private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];
    private engines: {
        preparation: ElementPreparationEngine;
        calculation: ElementCalculationEngine;
        rendering: ElementRenderingEngine;
    };

    // Render status per chart
    private renderStatus: Map<string, {
        isRendered: boolean;
        isVisible: boolean;
        renderTime: number;
        elementCount: number;
    }> = new Map();

    // Active planet highlights (chartId -> planet name)
    private activeHighlights: Map<string, string> = new Map();

    constructor(eventStream: EventStream) {
        this.eventStream = eventStream;

        // Initialize engines - shared instances for performance
        this.engines = {
            preparation: ElementPreparationEngine.getInstance(),
            calculation: ElementCalculationEngine.getInstance(),
            rendering: ElementRenderingEngine.getInstance()
        };

        this.setupEventSubscriptions();
        devLog('🌟 GrahaChakraRenderer: Initialized specialized planet renderer');
    }

    /**
     * Setup event subscriptions for graha rendering
     */
    private setupEventSubscriptions(): void {
        // Main render request
        this.eventSubscriptions.push(
            this.eventStream.subscribe('graha.render.request', (event: any) => {
                this.handleRenderRequest(event);
            })
        );

        // Toggle visibility
        this.eventSubscriptions.push(
            this.eventStream.subscribe('graha.toggle', (event: any) => {
                this.handleToggleVisibility(event);
            })
        );

        // Display mode changes
        this.eventSubscriptions.push(
            this.eventStream.subscribe('graha.displayMode.change', (event: any) => {
                this.handleDisplayModeChange(event);
            })
        );

        // Planet drag highlight events
        this.eventSubscriptions.push(
            this.eventStream.subscribe('planet.drag.ready', (event: any) => {
                this.handlePlanetDragStart(event);
            })
        );

        this.eventSubscriptions.push(
            this.eventStream.subscribe('planet.drag.end', (event: any) => {
                this.handlePlanetDragEnd(event);
            })
        );

        this.eventSubscriptions.push(
            this.eventStream.subscribe('planet.drag.cancel', (event: any) => {
                this.handlePlanetDragEnd(event);
            })
        );

        devLog('🔗 GrahaChakraRenderer: Event subscriptions configured');
    }

    /**
     * Cleanup event subscriptions and resources
     */
    public destroy(): void {
        this.eventSubscriptions.forEach(sub => sub.unsubscribe());
        this.eventSubscriptions = [];
        this.renderStatus.clear();
        this.activeHighlights.clear();
    }

    /**
     * Handle render request event
     */
    private async handleRenderRequest(event: GrahaRenderRequest): Promise<void> {
        // Performance timing available if needed

        devLog(`🎯 GrahaChakraRenderer: Render request for chart ${event.chartId}`);

        try {
            const result = await this.renderGraha(event);

            // Update render status
            this.renderStatus.set(event.chartId, {
                isRendered: true,
                isVisible: true,
                renderTime: performance.now(),
                elementCount: result.elementsRendered
            });

            // Emit completion event
            this.eventStream.emit({
                id: `graha-render-completed-${event.chartId}-${Date.now()}`,
                type: 'graha.render.completed',
                source: 'graha-chakra-renderer',
                chartId: event.chartId,
                timestamp: Date.now(),
                metadata: {
                    success: result.success,
                    elementsRendered: result.elementsRendered,
                    renderTime: result.renderTime
                }
            });

            devLog(`✅ GrahaChakraRenderer: Completed render for ${event.chartId} - ${result.elementsRendered} elements in ${result.renderTime.toFixed(2)}ms`);

        } catch (error) {
            devError(`❌ GrahaChakraRenderer: Render failed for ${event.chartId}:`, error);

            this.eventStream.emit({
                id: `graha-render-failed-${event.chartId}-${Date.now()}`,
                type: 'graha.render.failed',
                source: 'graha-chakra-renderer',
                chartId: event.chartId,
                timestamp: Date.now(),
                metadata: { error: error instanceof Error ? error.message : String(error) }
            });
        }
    }

    /**
     * Core graha rendering logic
     */
    private async renderGraha(request: GrahaRenderRequest): Promise<GrahaRenderResult> {
        const startTime = performance.now();
        const { chartId, targetSelector, data, options, containerBounds } = request;

        devLog(`🌟 GrahaChakraRenderer[${chartId}]: Starting render with ${data.planets?.length} planets`);

        // Set chart ID for rendering engine
        this.engines.rendering.setChartId(chartId);

        // Validate required data
        if (!data.planets || data.planets.length === 0) {
            throw new Error('No planets data provided');
        }

        if (!data.lagna) {
            throw new Error('No lagna data provided');
        }

        // HORA DEBUG: Log what we're processing
        if (options.debugMode) {
            devLog(`🌟 GrahaChakraRenderer[${chartId}]: HORA DEBUG:`, {
                horaData: data.hora,
                horaCurrentPlanet: data.hora?.currentPlanet,
                showHora: options.showHora
            });
        }

        // 1. Create UnifiedElement from raw API data
        const elements = (data.rawApiPlanets || [])
            .map(apiPlanet => {
                // Find corresponding processed planet data
                const processedPlanet = data.planets.find(p => p.graha.toString() === apiPlanet.short_name) ||
                                       (apiPlanet.short_name === 'Lg' ? data.lagna : null);

                if (!processedPlanet?.position) return null;

                // Use correct Vedic mapping: 0° = Mesha (containerIndex=1), 330° = Meena (containerIndex=0)
                const rasiIndex = Math.floor(apiPlanet.longitude / 30) % 12;
                const containerIndex = rasiIndex === 11 ? 0 : rasiIndex + 1;
                const houseNumber = rasiIndex + 1;  // House numbers are 1-12
                const degree = apiPlanet.longitude % 30;

                return {
                    id: `planet-${apiPlanet.short_name}`,
                    type: 'planet' as const,
                    name: apiPlanet.name,       // Full API name: "Surya", "Lagna", etc.
                    displayName: apiPlanet.short_name,  // Short name for display
                    position: processedPlanet,  // Processed planet data for calculations
                    containerIndex,
                    houseNumber,
                    degree,
                    special: 'none' as const   // Will be updated later for hora
                };
            })
            .filter(element => element !== null) as UnifiedElement[];

        devLog(`🔧 GrahaChakraRenderer[${chartId}]: Prepared ${elements.length} elements`);

        // 2. Calculate positions
        const containerBoundsArray = Array.from(containerBounds.values());
        if (containerBoundsArray.length === 0) {
            throw new Error('No container bounds available');
        }

        const positioned = this.engines.calculation.calculateElementPositions(
            elements,
            containerBoundsArray,
            options.displayMode
        );

        devLog(`📐 GrahaChakraRenderer[${chartId}]: Calculated positions for ${positioned.length} elements`);

        // 3. Get target layer
        const targetLayer = d3.select(targetSelector) as any;
        if (targetLayer.empty()) {
            throw new Error(`Target layer not found: ${targetSelector}`);
        }

        // 4. Prepare elements with ElementPreparationEngine
        try {
            const preparationInput = {
                planets: data.planets,
                lagna: data.lagna,
                hora: data.hora
            };

            const preparationResult = this.engines.preparation.prepareElements(preparationInput, {
                debugMode: options.debugMode || false,
                enableYogaGrouping: false,
                yogaGroupingMode: 0,
                degreeTolerance: 5,
                includeRetrogradePlanets: true
            });

            devLog(`✅ GrahaChakraRenderer[${chartId}]: ElementPreparationEngine completed:`, {
                elementsCreated: preparationResult.elements.length,
                preparationTime: preparationResult.statistics.preparationTime
            });

        } catch (error) {
            devError(`❌ GrahaChakraRenderer[${chartId}]: Element preparation failed:`, error);
        }

        // 5. Render elements
        const renderResult = this.engines.rendering.renderElements(
            positioned,
            targetLayer,
            undefined, // linesOverlay handled separately
            undefined,
            {
                chartId: chartId,
                mode: options.displayMode,
                showDegreelines: true,
                showYogaGroups: false,
                showPlanets: true,
                horaCurrentPlanet: data.hora?.currentPlanet,
                enableAnimations: options.enableAnimations,
                debugMode: options.debugMode || false
            }
        );

        const renderTime = performance.now() - startTime;

        devLog(`🎨 GrahaChakraRenderer[${chartId}]: Rendered ${renderResult.totalElements} elements in ${renderTime.toFixed(2)}ms`);

        return {
            success: true,
            elementsRendered: renderResult.totalElements,
            renderTime,
            chartId
        };
    }

    /**
     * Handle visibility toggle
     */
    private handleToggleVisibility(event: any): void {
        const { chartId, visible } = event;

        devLog(`👁️ GrahaChakraRenderer[${chartId}]: Toggle visibility to ${visible}`);

        const targetSelector = `.lines-overlay-${chartId}`;
        const layer = d3.select(targetSelector);

        if (!layer.empty()) {
            layer.style('display', visible ? 'block' : 'none');

            // Update status
            const status = this.renderStatus.get(chartId);
            if (status) {
                status.isVisible = visible;
                this.renderStatus.set(chartId, status);
            }
        } else {
            devWarn(`⚠️ GrahaChakraRenderer[${chartId}]: Target layer not found for visibility toggle`);
        }
    }

    /**
     * Handle display mode change
     */
    private async handleDisplayModeChange(event: any): Promise<void> {
        const { chartId, displayMode } = event;

        devLog(`🔄 GrahaChakraRenderer[${chartId}]: Display mode change to ${displayMode}`);

        // For display mode changes, we need to re-render with new mode
        // This would typically be triggered by zoom events
        // Implementation depends on how you want to handle mode changes
    }

    /**
     * Get render status for a chart
     */
    public getStatus(chartId: string) {
        return this.renderStatus.get(chartId) || {
            isRendered: false,
            isVisible: false,
            renderTime: 0,
            elementCount: 0
        };
    }

    /**
     * Get all render statuses
     */
    public getAllStatuses() {
        const statuses: { [chartId: string]: any } = {};
        this.renderStatus.forEach((status, chartId) => {
            statuses[chartId] = status;
        });
        return statuses;
    }

    /**
     * Clear render status for a chart
     */
    public clearStatus(chartId: string): void {
        this.renderStatus.delete(chartId);
        devLog(`🧹 GrahaChakraRenderer: Cleared status for chart ${chartId}`);
    }

    // =========================================================================
    // Planet Drag Highlight
    // =========================================================================

    /**
     * Handle planet drag start - change text colors and add golden background to dragged planet
     * Dragged planet: golden background
     * Chandra: green, Lagna: red, Surya: copper, others: black
     */
    private handlePlanetDragStart(event: any): void {
        const chartId = event.metadata?.chartId as string;
        const draggedPlanet = event.metadata?.planet as string;

        if (!chartId || !draggedPlanet) return;

        devLog(`✨ GrahaChakraRenderer[${chartId}]: Styling planets for drag, dragged: ${draggedPlanet}`);

        // Remove any existing highlights for this chart
        this.removePlanetHighlight(chartId);

        // Color mapping for specific planets
        const planetTextColors: Record<string, string> = {
            'Ch': '#228B22',      // Chandra - green
            'Lg': '#DC143C',      // Lagna - red
            'Sy': '#B87333',      // Surya - copper
        };
        const defaultTextColor = '#000000';  // Black for others
        const dragBackgroundColor = DEFAULT_PRESET.planetSprites.dragHighlightColor;

        // Get the parent group (lines-overlay)
        const parent = d3.select(`.lines-overlay-${chartId}`);
        if (parent.empty()) return;

        // Find all planet sprites and style them
        const allSprites = parent.selectAll('[class*="planet-sprite-"]');

        allSprites.each(function() {
            const sprite = d3.select(this);
            const classList = sprite.attr('class') || '';

            // Extract planet name from class
            const planetMatch = classList.match(/planet-sprite-(\w+)/);
            if (!planetMatch) return;

            const planetName = planetMatch[1];
            const isDragged = planetName === draggedPlanet;

            // Find text element within sprite
            const textEl = sprite.select('text');
            if (textEl.empty()) return;

            // Store original color for restoration
            const originalFill = textEl.attr('fill');
            if (originalFill && !textEl.attr('data-original-fill')) {
                textEl.attr('data-original-fill', originalFill);
            }

            // Set text color
            const textColor = planetTextColors[planetName] || defaultTextColor;
            textEl.attr('fill', textColor);

            // Add golden background for dragged planet
            if (isDragged) {
                // Get sprite position
                const transform = sprite.attr('transform');
                if (transform) {
                    const match = transform.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
                    if (match) {
                        const x = parseFloat(match[1]);
                        const y = parseFloat(match[2]);

                        parent.insert('circle', ':first-child')
                            .attr('class', `planet-drag-highlight planet-drag-highlight-${chartId}`)
                            .attr('cx', x)
                            .attr('cy', y)
                            .attr('r', 18)
                            .attr('fill', `${dragBackgroundColor}66`)
                            .attr('stroke', dragBackgroundColor)
                            .attr('stroke-width', 2)
                            .style('pointer-events', 'none');
                    }
                }
            }
        });

        // Store active highlight
        this.activeHighlights.set(chartId, draggedPlanet);

        devLog(`✨ GrahaChakraRenderer[${chartId}]: Planet styling applied`);
    }

    /**
     * Handle planet drag end - remove highlight
     */
    private handlePlanetDragEnd(event: any): void {
        const chartId = event.metadata?.chartId as string;
        if (!chartId) return;

        this.removePlanetHighlight(chartId);
    }

    /**
     * Remove all planet highlights for a chart
     */
    private removePlanetHighlight(chartId: string): void {
        const highlightSelector = `.planet-drag-highlight-${chartId}`;
        const highlights = d3.selectAll(highlightSelector);

        if (!highlights.empty()) {
            const count = highlights.size();
            highlights.remove();
            devLog(`🧹 GrahaChakraRenderer[${chartId}]: ${count} highlights removed`);
        }

        this.activeHighlights.delete(chartId);
    }
}